import { useCallback, useState } from 'react'
import { ErrorBanner, formatUtc } from '../../components/Async'
import { ApiError, alerts, type ObservedWindow } from '../../lib/api'

/**
 * What actually left the company, on the case rather than two clicks away from it.
 *
 * This is the single most valuable thing the redesign moves. The excerpt has existed since Phase 10
 * and has only ever been reachable from an event detail page — so an analyst triaging a case saw the
 * rule that matched and had to leave the case, find the right occurrence, and open a panel to see the
 * words. That is the difference between "an alert fired" and "here is what was sent", and it was the
 * one thing the queue could not tell anybody.
 *
 * Still folded shut, and that has not changed for the reason it never should: somebody reading a case
 * should see which rule matched without also seeing what a colleague typed, unless they decide they
 * need to. Opening it is now an act with consequences — it requires `prompt:read` rather than
 * `telemetry:read`, and it writes a row naming the reader (ADR-S-044). The button says so, because a
 * control that records your name should tell you before you press it rather than after.
 *
 * Three absences, told apart. An event that never carried a prompt, an excerpt whose words have aged
 * out of the stored body, and a read that failed are three different facts, and rendering any of them
 * as an empty block would be the D-263 defect on the screen that most depends on not committing it.
 */
export function CapturedExcerpt({ alertId }: { alertId: string }) {
  const [windows, setWindows] = useState<ObservedWindow[] | null>(null)
  const [redactedUtc, setRedactedUtc] = useState<string | null>(null)
  const [matched, setMatched] = useState(true)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const show = useCallback(async () => {
    setBusy(true)
    setFailure(null)
    try {
      const answer = await alerts.excerpt(alertId)
      setWindows(answer.windows)
      setRedactedUtc(answer.bodyRedactedUtc ?? null)
      setMatched(answer.matched)
    } catch (error) {
      setFailure(
        error instanceof ApiError && error.status === 403
          ? 'Reading what somebody typed needs more than read access. An analyst, an administrator or the owner can open this.'
          : error instanceof ApiError
            ? error.message
            : 'The excerpt could not be read.'
      )
    } finally {
      setBusy(false)
    }
  }, [alertId])

  const prose = windows?.flatMap((window) => (window.promptText ? [window] : [])) ?? []

  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Captured</h3>

        <ErrorBanner message={failure} />

        {windows === null ? (
          <>
            <button type="button" onClick={show} disabled={busy} data-testid="show-excerpt">
              {busy ? 'Reading…' : 'Show what was captured'}
            </button>
            <div className="hint">
              Read from the body this device sent, which the server keeps byte-exact. It is not part of
              the event record, so it appears in no query result and no SIEM export — the only way to
              see it is to ask here, and asking is recorded against your account.
            </div>
          </>
        ) : redactedUtc !== null ? (
          /*
            The words aged out. Deliberately not the same sentence as "there was no prompt": one of
            those is about retention working and the other is about the capability. The rules that
            matched survive the sweep, so they are still shown - which is strictly more useful than an
            empty block and is the reason the sweep leaves them.
          */
          <div className="empty" data-testid="excerpt-redacted">
            What was typed has aged out of this record.
            <div className="hint">
              Removed {formatUtc(redactedUtc)}, past this organization&apos;s prompt window. The rules
              that matched are kept, so the case still says what fired — the words it fired on are
              gone, which is what that window is for.
            </div>
            <MatchedTerms windows={windows} />
          </div>
        ) : prose.length === 0 ? (
          <div className="empty" data-testid="no-excerpt">
            This case carries no captured text.
            <div className="hint">
              {matched
                ? 'The observation behind this case captured no text. The policy may redact it, or the control may have been empty when the agent looked.'
                : // The common case, and worth wording precisely. A policy that enforces without
                  // publishing reports that a rule fired and never sends what it fired on, which is a
                  // deliberate setting rather than a failure - so this says which setting, not "none".
                  'Nothing was published for this case. A policy that enforces without publishing its result reports that a rule fired and never sends the words it fired on; ticking "send every result" on the prompt policy is what changes that.'}
            </div>
          </div>
        ) : (
          <div data-testid="excerpt">
            {prose.map((window, index) => (
              <div key={index} className="subsection">
                <span className="eyebrow">Captured · redacted on device</span>
                {/*
                  A blockquote in italic, which is the design's instruction and is not decoration: it
                  marks the text as somebody else's words rather than as the console's own, on the one
                  screen where confusing those two would matter.
                */}
                <blockquote className="excerpt" data-testid="excerpt-text">
                  {window.promptText}
                </blockquote>
                <div className="muted">
                  {window.windowTitle ?? 'an untitled window'}
                  {window.processName && ` · ${window.processName}`}
                  {window.extractionMethod && ` · read by ${window.extractionMethod}`}
                </div>
              </div>
            ))}
            <MatchedTerms windows={windows} />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The dictionary terms that fired, as chips.
 *
 * Shown after a sweep as well as before, because the rule names survive it — so a case whose words
 * have gone still answers "what was this about". The terms themselves are redacted with the prose, so
 * a chip reading `[redacted]` is honest rather than broken: the rule is named, the span it matched is
 * not.
 */
function MatchedTerms({ windows }: { windows: ObservedWindow[] }) {
  const matches = windows.flatMap((window) => window.matches)
  if (matches.length === 0) return null

  return (
    <div className="matched-terms" data-testid="matched-terms">
      <span className="eyebrow">Matched</span>
      <div className="chips">
        {matches.map((match, index) => (
          <span key={index} className="term-chip">
            {match.ruleName ?? 'an unnamed rule'}
            {match.term && match.term !== '[redacted]' && <span className="muted"> · {match.term}</span>}
          </span>
        ))}
      </div>
    </div>
  )
}
